// src/pages/VertraegeEditor.jsx
// Vertraege einer Station (nur Buero/Admin): Wartungsvertrag,
// Betriebsfuehrungsvertrag, Bereitschaft (Notdienst).
//
// Anna-Lena, 17.09.2026: Der Vertrag haengt an der Station. Betriebsfuehrung
// und Bereitschaft brauchen nur "vorhanden", Dauer und das PDF. Beim
// Wartungsvertrag die Angaben aus dem Vertragsmuster (Buchbusch). Kuendigungs-
// fristen werden bewusst NICHT erfasst ("wir kuendigen eigentlich keine Vertraege").
//
// Gespeichert wird mit einem eigenen Knopf (das Buero arbeitet online) - nicht
// ueber die Warteschlange des Protokolls.

import { useState, useEffect, useCallback, useRef } from 'react';
import { C } from './formui';
import {
  VERTRAGSARTEN, ZYKLUS_JAHRE, LEISTUNGEN, leistungGewaehlt, OELPROBE_VERTRAG, ABSCHALTZEITEN, MONATE_KURZ,
  vertraegeDerStation, vertragSpeichern, vertragPdfHochladen, vertragPdfLoeschen, vertragPdfOeffnen,
  istMigrationFehlt,
} from '../lib/vertraege';

// Kompakte Buero-Ansicht (Anna-Lena, 18.09.2026: "elegant und minimalistisch",
// wird fast nur im Buero am PC genutzt). Kleine Felder im Raster, ruhige
// Abschnitte, Vertragsarten als Schalter; nicht gewaehlte Arten nur eine Zeile.
const T = {
  rand: '1px solid ' + C.gray200,
  label: { display: 'block', fontSize: 11.5, fontWeight: 600, color: C.gray500, letterSpacing: 0.3, marginBottom: 4 },
  feld: { width: '100%', boxSizing: 'border-box', height: 34, padding: '0 10px', fontSize: 14, border: '1px solid ' + C.gray200,
          borderRadius: 6, fontFamily: 'inherit', background: C.white, color: C.navy, outline: 'none' },
};

function Feld({ label, children, breit }) {
  return (
    <label style={{ display: 'block', minWidth: 0, gridColumn: breit ? '1 / -1' : undefined }}>
      <span style={T.label}>{label}</span>
      {children}
    </label>
  );
}
function Raster({ children, min = 170 }) {
  return <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: '12px 16px' }}>{children}</div>;
}
function Eingabe({ value, onChange, placeholder, type = 'text' }) {
  return <input type={type} value={value ?? ''} placeholder={placeholder} onChange={e => onChange(e.target.value)} style={T.feld}/>;
}
function Zahl({ value, onChange, placeholder }) {
  return <input type="number" min="0" value={value ?? ''} placeholder={placeholder}
    onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))} style={T.feld}/>;
}
function Auswahl({ value, onChange, optionen, leer = '–' }) {
  // Gespeicherter Wert, den die Liste nicht mehr kennt (z. B. 5 Jahre): sichtbar lassen.
  if (value && !optionen.some(o => o.value === value)) {
    optionen = [{ value, label: (value === '1' ? 'jährlich' : 'alle ' + value + ' Jahre') + ' (nicht mehr vorgesehen)' }, ...optionen];
  }
  return (
    <select value={value ?? ''} onChange={e => onChange(e.target.value || null)} style={{ ...T.feld, paddingRight: 4 }}>
      <option value="">{leer}</option>
      {optionen.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
function MonatFeld({ value, onChange }) {
  return <input type="month" value={value ? String(value).slice(0, 7) : ''} onChange={e => onChange(e.target.value ? e.target.value + '-01' : null)} style={T.feld}/>;
}
function DatumFeldEinfach({ value, onChange }) {
  return <input type="date" value={value || ''} onChange={e => onChange(e.target.value || null)} style={T.feld}/>;
}
function Mehrzeilig({ value, onChange, placeholder, zeilen = 2 }) {
  return <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={zeilen}
    style={{ ...T.feld, height: 'auto', padding: '7px 10px', resize: 'vertical', lineHeight: 1.4 }}/>;
}
function Chip({ an, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      style={{ height: 28, padding: '0 11px', borderRadius: 14, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
               border: '1px solid ' + (an ? C.navy : C.gray200), background: an ? C.navy : C.white, color: an ? C.white : C.gray700 }}>
      {children}
    </button>
  );
}
function Haken({ checked, onChange, children }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, color: C.navy, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} style={{ width: 16, height: 16, accentColor: C.navy, cursor: 'pointer' }}/>
      {children}
    </label>
  );
}
function Schalter({ an, onChange }) {
  return (
    <button type="button" role="switch" aria-checked={an} onClick={() => onChange(!an)}
      style={{ width: 38, height: 22, borderRadius: 11, border: 'none', padding: 2, cursor: 'pointer', flexShrink: 0,
               background: an ? C.green : C.gray300, transition: 'background .15s' }}>
      <span style={{ display: 'block', width: 18, height: 18, borderRadius: 9, background: C.white,
                     transform: an ? 'translateX(16px)' : 'none', transition: 'transform .15s' }}/>
    </button>
  );
}
function Abschnitt({ titel, children }) {
  return (
    <div style={{ paddingTop: 16, marginTop: 16, borderTop: T.rand }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.gray500, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>{titel}</div>
      {children}
    </div>
  );
}

const umschalten = (liste, wert) => {
  const l = Array.isArray(liste) ? liste : [];
  return l.includes(wert) ? l.filter(x => x !== wert) : [...l, wert];
};
const zyklusOptionen = ZYKLUS_JAHRE.map(j => ({ value: String(j), label: j === 1 ? 'jährlich' : 'alle ' + j + ' Jahre' }));

function PdfZeile({ stationId, art, vertrag, onPdf, onFehler, onLaedt }) {
  const [laedt, setLaedtIntern] = useState(false);
  const setLaedt = (x) => { setLaedtIntern(x); if (onLaedt) onLaedt(x); };
  const waehlen = async (e) => {
    const datei = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!datei) return;
    setLaedt(true);
    try {
      const neu = await vertragPdfHochladen(stationId, art, datei);
      const alt = vertrag.pdf_pfad;
      // Sofort am Vertrag speichern (nur die PDF-Angaben) - sonst zeigte der
      // Vertrag nach Verlassen ohne "Speichern" auf eine geloeschte Datei.
      await onPdf(neu);
      if (alt && alt !== neu.pdf_pfad) vertragPdfLoeschen(alt);
    } catch (err) {
      onFehler('PDF nicht hochgeladen: ' + (err.message || err));
    } finally {
      setLaedt(false);
    }
  };
  const link = { background: 'none', border: 'none', padding: 0, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      {vertrag.pdf_pfad && (
        <button type="button" onClick={() => vertragPdfOeffnen(vertrag.pdf_pfad).catch(err => onFehler('PDF lässt sich nicht öffnen: ' + err.message))}
          title={vertrag.pdf_name || ''}
          style={{ ...link, color: C.blue, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          PDF öffnen
        </button>
      )}
      <label style={{ ...link, color: laedt ? C.gray500 : C.gray700, cursor: laedt ? 'wait' : 'pointer' }}>
        {laedt ? 'lädt hoch …' : vertrag.pdf_pfad ? 'ersetzen' : '+ PDF'}
        <input type="file" accept="application/pdf" onChange={waehlen} disabled={laedt} style={{ display: 'none' }}/>
      </label>
    </span>
  );
}

export default function VertraegeEditor({ stationId, onGespeichert, onAenderungen }) {
  const [vertraege, setVertraege] = useState({});      // art -> Formularstand
  const [geaendert, setGeaendert] = useState({});      // art -> true
  const [laedt, setLaedt] = useState(true);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [migrationFehlt, setMigrationFehlt] = useState(false);
  const [gespeichert, setGespeichert] = useState(false);
  const [pdfLaedt, setPdfLaedt] = useState(0);   // laufende PDF-Uploads
  const [ladeFehler, setLadeFehler] = useState(null);
  // Stand aus der Datenbank je Art: Gesendet werden nur Abweichungen davon
  // (sonst ueberschrieb ein aelterer Formularstand z. B. "Sichtpruefung erledigt").
  const geladen = useRef({});

  const laden = useCallback(async () => {
    if (!stationId) return;
    setLaedt(true); setFehler(null); setLadeFehler(null);
    try {
      const daten = await vertraegeDerStation(stationId);
      geladen.current = daten;
      setVertraege(daten);
      setGeaendert({});
      setMigrationFehlt(false);
    } catch (e) {
      if (istMigrationFehlt(e)) setMigrationFehlt(true);
      // Kein leeres Formular zeigen - Speichern wuerde sonst den echten Vertrag ueberschreiben.
      else setLadeFehler('Verträge konnten nicht geladen werden: ' + (e.message || e));
    } finally {
      setLaedt(false);
    }
  }, [stationId]);
  useEffect(() => { laden(); }, [laden]);

  const setFeld = (art, patch) => {
    setVertraege(alt => ({ ...alt, [art]: { ...(alt[art] || { vorhanden: false }), ...patch } }));
    setGeaendert(alt => ({ ...alt, [art]: true }));
    setGespeichert(false);
  };

  const pdfGespeichert = async (art, pdf) => {
    const zeile = await vertragSpeichern(stationId, art, { ...pdf, vorhanden: true });
    geladen.current = { ...geladen.current, [art]: { ...(geladen.current[art] || zeile), id: zeile.id, vorhanden: true, pdf_pfad: zeile.pdf_pfad, pdf_name: zeile.pdf_name } };
    setVertraege(alt => ({ ...alt, [art]: { ...(alt[art] || {}), id: zeile.id, vorhanden: true, pdf_pfad: zeile.pdf_pfad, pdf_name: zeile.pdf_name } }));
  };

  const speichern = async () => {
    if (speichert) return;
    setSpeichert(true); setFehler(null);
    try {
      const arten = Object.keys(geaendert);
      const ergebnis = {};
      for (const art of arten) {
        const v = vertraege[art];
        // Nie angelegt und nicht angehakt: nichts zu speichern.
        if (!v || (!v.id && !v.vorhanden)) continue;
        // PDF-Angaben schreibt NUR der Upload - sonst koennte ein aelterer Stand
        // hier einen gerade hochgeladenen Vertrag wieder ueberschreiben.
        const { pdf_pfad, pdf_name, ...felder } = v;
        const basis = geladen.current[art];
        let senden = felder;
        if (basis?.id) {
          senden = { id: basis.id };
          for (const [k, wert] of Object.entries(felder)) {
            if (JSON.stringify(wert ?? null) !== JSON.stringify(basis[k] ?? null)) senden[k] = wert;
          }
        }
        ergebnis[art] = await vertragSpeichern(stationId, art, senden);
        geladen.current = { ...geladen.current, [art]: ergebnis[art] };
      }
      // Mit dem aktuellen Stand zusammenfuehren (PDF-Upload kann dazwischen gekommen sein).
      setVertraege(alt => {
        const neu = { ...alt };
        for (const [art, zeile] of Object.entries(ergebnis)) {
          neu[art] = { ...zeile, pdf_pfad: alt[art]?.pdf_pfad ?? zeile.pdf_pfad, pdf_name: alt[art]?.pdf_name ?? zeile.pdf_name };
        }
        return neu;
      });
      setGeaendert(alt => { const n = { ...alt }; arten.forEach(a => { delete n[a]; }); return n; });
      setGespeichert(true);
      if (onGespeichert) onGespeichert(ergebnis);
    } catch (e) {
      setFehler('Nicht gespeichert: ' + (e.message || e));
    } finally {
      setSpeichert(false);
    }
  };

  if (!stationId) {
    return <div style={{ padding: 14, color: C.gray500, fontSize: 15 }}>Bitte die Station zuerst speichern – danach lassen sich Verträge hinterlegen.</div>;
  }
  if (laedt) return <div style={{ padding: 20, color: C.gray500, fontSize: 15 }}>Verträge werden geladen …</div>;
  if (ladeFehler) {
    return (
      <div style={{ background: C.redBg, borderRadius: 8, padding: 14, fontSize: 15, color: C.navy }}>
        {ladeFehler}
        <button type="button" onClick={laden} style={{ marginLeft: 10, minHeight: 40, padding: '6px 14px', borderRadius: 8, border: '1.5px solid ' + C.navy, background: C.white, color: C.navy, fontWeight: 700, cursor: 'pointer' }}>Nochmal laden</button>
      </div>
    );
  }
  if (migrationFehlt) {
    return (
      <div style={{ background: C.yellowBg, borderLeft: '4px solid ' + C.yellow, borderRadius: 8, padding: 14, fontSize: 15, color: C.navy, lineHeight: 1.5 }}>
        <strong>Noch nicht eingerichtet:</strong> Für die Verträge muss in Supabase zuerst die Datei
        <code> 45-vertraege.sql</code> ausgeführt werden.
      </div>
    );
  }

  const offen = Object.keys(geaendert).length > 0;
  if (onAenderungen) onAenderungen(offen);

  return (
    <div style={{ color: C.navy }}>
      <fieldset disabled={speichert} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <div style={{ border: T.rand, borderRadius: 8, background: C.white, overflow: 'hidden' }}>
      {VERTRAGSARTEN.map(({ art, text }, idx) => {
        const v = vertraege[art] || { vorhanden: false };
        const an = !!v.vorhanden;
        const set = (patch) => setFeld(art, patch);
        const l = v.leistungen || {};
        const kurz = [
          v.beginn && 'seit ' + String(v.beginn).slice(0, 10).split('-').reverse().join('.'),
          v.laufzeit_jahre && v.laufzeit_jahre + ' J. Laufzeit',
          art === 'wartung' && v.wartungszyklus_jahre && (v.wartungszyklus_jahre === 1 ? 'jährlich' : 'alle ' + v.wartungszyklus_jahre + ' J.'),
        ].filter(Boolean).join(' · ');
        return (
          <div key={art} style={{ borderTop: idx ? T.rand : 'none' }}>
            {/* Kopfzeile: Schalter, Name, Kurzinfo, PDF */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', flexWrap: 'wrap',
                          background: an ? C.white : '#FAFBFD' }}>
              <Schalter an={an} onChange={x => set({ vorhanden: x })}/>
              <span style={{ fontSize: 15, fontWeight: 700, color: an ? C.navy : C.gray500 }}>{text}</span>
              {an && kurz && <span style={{ fontSize: 13, color: C.gray500 }}>{kurz}</span>}
              {!an && v.id && <span style={{ fontSize: 12, color: C.gray500 }}>inaktiv – Angaben bleiben erhalten</span>}
              {an && (
                <span style={{ marginLeft: 'auto' }}>
                  <PdfZeile stationId={stationId} art={art} vertrag={v} onPdf={(pdf) => pdfGespeichert(art, pdf)} onFehler={setFehler}
                    onLaedt={(x) => setPdfLaedt(n => Math.max(0, n + (x ? 1 : -1)))}/>
                </span>
              )}
            </div>

            {an && (
              <div style={{ padding: '4px 16px 16px 66px' }}>
                <Raster>
                  <Feld label="Vertragsbeginn"><DatumFeldEinfach value={v.beginn} onChange={x => set({ beginn: x })}/></Feld>
                  <Feld label="Laufzeit (Jahre)"><Zahl value={v.laufzeit_jahre} onChange={x => set({ laufzeit_jahre: x })} placeholder="unbefristet"/></Feld>
                  {art !== 'wartung' && <Feld label="Notiz"><Eingabe value={v.notiz} onChange={x => set({ notiz: x })}/></Feld>}
                </Raster>

                {art === 'wartung' && (
                  <>
                    <Abschnitt titel="Intervalle">
                      <Raster>
                        <Feld label="Wartung"><Auswahl value={v.wartungszyklus_jahre ? String(v.wartungszyklus_jahre) : ''} onChange={x => set({ wartungszyklus_jahre: x ? Number(x) : null })} optionen={zyklusOptionen}/></Feld>
                        <Feld label="Sichtprüfung"><Auswahl value={v.sichtpruefung_jahre ? String(v.sichtpruefung_jahre) : ''} onChange={x => set({ sichtpruefung_jahre: x ? Number(x) : null })} optionen={zyklusOptionen} leer="keine"/></Feld>
                        <Feld label="Erste Wartung"><MonatFeld value={v.erste_wartung} onChange={x => set({ erste_wartung: x })}/></Feld>
                        <Feld label="Erste Sichtprüfung"><MonatFeld value={v.erste_sichtpruefung} onChange={x => set({ erste_sichtpruefung: x })}/></Feld>
                        <Feld label="Letzte Sichtprüfung"><MonatFeld value={v.letzte_sichtpruefung} onChange={x => set({ letzte_sichtpruefung: x })}/></Feld>
                      </Raster>
                    </Abschnitt>

                    <Abschnitt titel="Konditionen (netto)">
                      <Raster>
                        <Feld label="Entgelt je Wartung (€)"><Eingabe value={v.entgelt_wartung} onChange={x => set({ entgelt_wartung: x })} placeholder="2.315,00"/></Feld>
                        <Feld label="Entgelt Sichtprüfung (€)"><Eingabe value={v.entgelt_sichtpruefung} onChange={x => set({ entgelt_sichtpruefung: x })} placeholder="225,00"/></Feld>
                        <Feld label="Zahlungsziel"><Eingabe value={v.zahlungsziel} onChange={x => set({ zahlungsziel: x })} placeholder="14 Tage ohne Abzug"/></Feld>
                        <Feld label="Preisbindung (Jahre)"><Zahl value={v.preisbindung_jahre} onChange={x => set({ preisbindung_jahre: x })}/></Feld>
                      </Raster>
                    </Abschnitt>

                    <Abschnitt titel="Leistungsumfang">
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        {LEISTUNGEN.map(le => (
                          <Chip key={le.key} an={leistungGewaehlt(l, le.key)} onClick={() => set({ leistungen: { ...l, [le.key]: !leistungGewaehlt(l, le.key) } })}>{le.text}</Chip>
                        ))}
                        <span style={{ width: 210, marginLeft: 4 }}>
                          <Auswahl value={l.oelprobe} onChange={x => set({ leistungen: { ...l, oelprobe: x } })} optionen={OELPROBE_VERTRAG} leer="Ölprobe: –"/>
                        </span>
                      </div>
                    </Abschnitt>

                    <Abschnitt titel="Abschaltung beim Kunden">
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                        {ABSCHALTZEITEN.map(a => (
                          <Chip key={a.key} an={(v.abschaltzeiten || []).includes(a.key)} onClick={() => set({ abschaltzeiten: umschalten(v.abschaltzeiten, a.key) })}>{a.text}</Chip>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                        <span style={{ ...T.label, marginBottom: 0, marginRight: 6 }}>Bevorzugte Monate</span>
                        {MONATE_KURZ.map((m, i) => {
                          const sel = (v.bevorzugte_monate || []).includes(i + 1);
                          return (
                            <button key={m} type="button" onClick={() => set({ bevorzugte_monate: umschalten(v.bevorzugte_monate, i + 1).sort((a, b) => a - b) })}
                              style={{ width: 38, height: 26, borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0,
                                       border: '1px solid ' + (sel ? C.navy : C.gray200), background: sel ? C.navy : C.white, color: sel ? C.white : C.gray700 }}>
                              {m}
                            </button>
                          );
                        })}
                      </div>
                      <Eingabe value={v.abschalt_notiz} onChange={x => set({ abschalt_notiz: x })} placeholder="Hinweis zur Abschaltung, z. B. Produktion steht im August"/>
                    </Abschnitt>

                    <Abschnitt titel="Absprachen">
                      <div style={{ display: 'flex', gap: '8px 22px', flexWrap: 'wrap', marginBottom: 12 }}>
                        <Haken checked={v.freigabe_schriftlich} onChange={x => set({ freigabe_schriftlich: x })}>schriftliche Freigabe nötig</Haken>
                        <Haken checked={v.kunde_informiert_vnb} onChange={x => set({ kunde_informiert_vnb: x })}>Kunde informiert Netzbetreiber</Haken>
                        <Haken checked={v.kunde_stellt_schaltberechtigten} onChange={x => set({ kunde_stellt_schaltberechtigten: x })}>Kunde stellt Schaltberechtigten</Haken>
                      </div>
                      <Raster min={260}>
                        <Feld label="Hinweis für den Monteur (ohne Preise)"><Mehrzeilig value={v.hinweis_monteur} onChange={x => set({ hinweis_monteur: x })} placeholder="z. B. Schlüssel beim Pförtner"/></Feld>
                        <Feld label="Besondere Vereinbarungen (nur Büro)"><Mehrzeilig value={v.besondere_vereinbarungen} onChange={x => set({ besondere_vereinbarungen: x })}/></Feld>
                        <Feld label="Notiz"><Mehrzeilig value={v.notiz} onChange={x => set({ notiz: x })}/></Feld>
                      </Raster>
                    </Abschnitt>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
      </div>
      </fieldset>

      {/* Speichern: schlicht, rechts */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 12 }}>
        {fehler && <span style={{ color: C.red, fontSize: 13, fontWeight: 600, marginRight: 'auto' }}>{fehler}</span>}
        {offen && !speichert && <span style={{ fontSize: 13, color: C.orangeText }}>ungespeicherte Änderungen</span>}
        {gespeichert && !offen && <span style={{ fontSize: 13, color: C.green }}>✓ gespeichert</span>}
        <button type="button" onClick={speichern} disabled={!offen || speichert || pdfLaedt > 0}
          style={{ height: 36, padding: '0 18px', borderRadius: 6, border: 'none', fontWeight: 700, fontSize: 14,
                   background: !offen || speichert || pdfLaedt > 0 ? C.gray200 : C.navy,
                   color: !offen || speichert || pdfLaedt > 0 ? C.gray500 : C.white,
                   cursor: !offen || speichert || pdfLaedt > 0 ? 'default' : 'pointer' }}>
          {speichert ? 'speichert …' : pdfLaedt > 0 ? 'PDF lädt …' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}
